// src/pages/IntCarriers.js
import React, { useEffect, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Spinner,
} from "reactstrap"
import TableContainer from "../../components/Common/TableContainer"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import axios from "axios"
import apiURl, { baseURl } from "url"

const InternationalCarries = () => {
  document.title = "International Carriers"
  const navigate = useNavigate()

  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editID, setEditID] = useState(null)

  const [name, setName] = useState("")

  // ✅ Logo states
  const [logoFile, setLogoFile] = useState(null) // new upload
  const [logoPreview, setLogoPreview] = useState("") // preview for chosen file
  const [existingLogoUrl, setExistingLogoUrl] = useState("") // preview for edit

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedCarrierId, setSelectedCarrierId] = useState(null)

  // If your API returns only relative paths like "uploads/x.png",
  // you might need to prefix with apiURl or your CDN base.
  const resolveLogoUrl = carrier => {
    const raw = carrier?.logo
    if (!raw) return ""

    // If API already sends full URL
    if (/^https?:\/\//i.test(raw)) return raw

    // Otherwise prepend BASE URL
    return `${baseURl}${raw}`
  }

  const fetchCarriers = async () => {
    try {
      setIsLoading(true)
      const { data } = await axios.get(`${apiURl}/carriers/list`)
      if (data.status === "success") {
        setRows(data.carriers || [])
      } else {
        toastr.error(data.message || "Failed to fetch carriers")
      }
    } catch (err) {
      toastr.error("Error fetching carriers")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCarriers()
  }, [])

  const resetForm = () => {
    setName("")
    setEditID(null)

    // ✅ reset logo inputs
    setLogoFile(null)
    setLogoPreview("")
    setExistingLogoUrl("")

    setModalOpen(false)
  }

  const openAdd = () => {
    setEditID(null)
    setName("")
    setLogoFile(null)
    setLogoPreview("")
    setExistingLogoUrl("")
    setModalOpen(true)
  }

  const openEdit = row => {
    setEditID(row.id)
    setName(row.name || "")

    // ✅ edit mode: show existing logo (if any)
    const url = resolveLogoUrl(row)
    setExistingLogoUrl(url)

    // reset new file selection
    setLogoFile(null)
    setLogoPreview("")

    setModalOpen(true)
  }

  const onLogoChange = e => {
    const file = e.target.files?.[0] || null
    setLogoFile(file)

    if (logoPreview) URL.revokeObjectURL(logoPreview)

    if (file) {
      setLogoPreview(URL.createObjectURL(file))
    } else {
      setLogoPreview("")
    }
  }

  const saveCarrier = async e => {
    e.preventDefault()

    if (!name?.trim()) {
      toastr.error("Name is required")
      return
    }

    try {
      const url = editID
        ? `${apiURl}/carriers/update`
        : `${apiURl}/carriers/add`

      // ✅ multipart/form-data for image upload
      const formData = new FormData()
      formData.append("name", name.trim())
      if (editID) formData.append("id", editID)

      // only append if user selected a new image
      if (logoFile) formData.append("logo", logoFile)

      const { data } = await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      if (data.status === "success") {
        toastr.success(editID ? "Carrier updated" : "Carrier added")
        resetForm()
        fetchCarriers()
      } else {
        toastr.error(data.message || "Failed to save carrier")
      }
    } catch (err) {
      toastr.error("Error saving carrier")
    }
  }

  const handleDelete = () => {
    if (selectedCarrierId) {
      deleteCarrier(selectedCarrierId)
    }
    setShowDeleteModal(false)
  }

  const deleteCarrier = async id => {
    try {
      const { data } = await axios.post(`${apiURl}/carriers/delete`, { id })
      if (data.status === "success") {
        toastr.success("Carrier deleted")
        fetchCarriers()
      } else {
        toastr.error(data.message || "Failed to delete carrier")
      }
    } catch {
      toastr.error("Error deleting carrier")
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "Logo",
        accessor: "logo", // just for filtering; actual render uses row.original
        disableFilters: true,
        Cell: ({ row }) => {
          const url = resolveLogoUrl(row.original)
          return url ? (
            <img
              src={url}
              alt={row.original?.name || "logo"}
              style={{
                width: 40,
                height: 40,
                objectFit: "contain",
                borderRadius: 6,
                background: "#fff",
                border: "1px solid #eee",
                padding: 4,
              }}
              onError={e => {
                e.currentTarget.style.display = "none"
              }}
            />
          ) : (
            <span className="text-muted">—</span>
          )
        },
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        Cell: ({ row }) => {
          const { id, status } = row.original
          const isActive = Number(status) === 1
          const handleToggle = async () => {
            const newStatus = isActive ? 0 : 1
            try {
              const { data } = await axios.post(`${apiURl}/carriers/status`, {
                id,
                status: newStatus,
              })
              if (data.status === "success") {
                toastr.success("Status updated successfully")
                fetchCarriers()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }

          return (
            <div className="form-check form-switch mb-0">
              <input
                type="checkbox"
                role="switch"
                className="form-check-input"
                id={`carrier-switch-${id}`}
                defaultChecked={isActive}
                onChange={handleToggle}
              />
              <label
                className="form-check-label ms-2 mb-0"
                htmlFor={`carrier-switch-${id}`}
              >
                {isActive ? (
                  <span className="badge rounded-pill bg-success">Active</span>
                ) : (
                  <span className="badge rounded-pill bg-danger">Inactive</span>
                )}
              </label>
            </div>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        disableFilters: true,
        Cell: ({ row, value }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => openEdit(row.original)}
            >
              <i className="bx bx-pencil"></i> Edit
            </Button>
            <Button
              color="danger"
              size="sm"
              onClick={() => {
                setSelectedCarrierId(row.original.id)
                setShowDeleteModal(true)
              }}
            >
              <i className="bx bx-trash"></i> Delete
            </Button>
            <Button
              color="success"
              size="sm"
              onClick={() => navigate(`/carriers/${value}/zones`)}
            >
              <i className="bx bx-world"></i> Zones
            </Button>
            <Button
              color="warning"
              size="sm"
              onClick={() => navigate(`/carriers/${value}/rates`)}
            >
              <i className="bx bx-dollar"></i> Rates
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <h5 className="mt-2">International Carriers Management</h5>
          </Col>
          <Col className="text-end">
            <Button color="primary" className="btn-sm w-md" onClick={openAdd}>
              + Add Carrier
            </Button>
          </Col>
        </Row>

        <Row className="pt-3">
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="d-flex flex-column align-items-center justify-content-center py-4">
                    <Spinner
                      color="primary"
                      style={{ width: "3rem", height: "3rem" }}
                      className="mb-2"
                    />
                    <small className="text-muted">Loading ....</small>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={rows}
                    customPageSize={10}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        centered
      >
        <Form onSubmit={saveCarrier}>
          <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
            {editID ? "Edit Carrier" : "Add Carrier"}
          </ModalHeader>

          <ModalBody>
            <FormGroup>
              <Label>Carrier Name *</Label>
              <Input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </FormGroup>

            {/* ✅ Logo Upload */}
            <FormGroup className="mt-3">
              <Label>Carrier Logo {editID ? "(optional - replace)" : ""}</Label>
              <Input type="file" accept="image/*" onChange={onLogoChange} />
              <div className="mt-2 d-flex gap-3 align-items-center flex-wrap">
                {/* preview selected */}
                {logoPreview ? (
                  <div>
                    <small className="text-muted d-block">
                      New logo preview
                    </small>
                    <img
                      src={logoPreview}
                      alt="new logo preview"
                      style={{
                        width: 90,
                        height: 90,
                        objectFit: "contain",
                        borderRadius: 10,
                        border: "1px solid #eee",
                        padding: 8,
                        background: "#fff",
                      }}
                    />
                  </div>
                ) : null}

                {/* preview existing (edit mode only, if no new file picked) */}
                {!logoPreview && existingLogoUrl ? (
                  <div>
                    <small className="text-muted d-block">Current logo</small>
                    <img
                      src={existingLogoUrl}
                      alt="existing logo"
                      style={{
                        width: 90,
                        height: 90,
                        objectFit: "contain",
                        borderRadius: 10,
                        border: "1px solid #eee",
                        padding: 8,
                        background: "#fff",
                      }}
                      onError={e => {
                        e.currentTarget.style.display = "none"
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={resetForm}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editID ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        toggle={() => setShowDeleteModal(!showDeleteModal)}
        centered
      >
        <ModalHeader toggle={() => setShowDeleteModal(!showDeleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this carrier? This action cannot be
          undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowDeleteModal(!showDeleteModal)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default InternationalCarries
